import { backendApi } from "@/services/backend/api";
import { VisualAssessmentCatalog } from "@/services/backend/htz/inspection/types/visual-assessment-catalog";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { UUID } from "@bitory-gmbh/nidavellir";

const visualAssessmentCatalogApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "HtzVisualAssessmentCatalog",
      "HtzActiveVisualAssessmentCatalog",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzVisualAssessmentCatalogPut: build.mutation<
        VisualAssessmentCatalog,
        PutRequest
      >({
        query: (request) => ({
          url: `/htz/inspection/visual-assessment-catalog`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzVisualAssessmentCatalog", id: request.id },
          "HtzVisualAssessmentCatalog",
        ],
      }),
      htzVisualAssessmentCatalogShow: build.query<
        VisualAssessmentCatalog,
        ShowRequest
      >({
        query: (request) => ({
          url: `/htz/inspection/visual-assessment-catalog`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzVisualAssessmentCatalog"),
      }),
      htzVisualAssessmentCatalogList: build.query<
        Lst<VisualAssessmentCatalog>,
        ListRequest
      >({
        query: (request) => ({
          url: `/htz/inspection/visual-assessment-catalog/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.items, "HtzVisualAssessmentCatalog"),
      }),
      htzVisualAssessmentCatalogDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/htz/inspection/visual-assessment-catalog`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["HtzVisualAssessmentCatalog"],
      }),
      htzVisualAssessmentCatalogPutActiveCatalog: build.mutation<
        ActiveCatalog,
        ActiveCatalog
      >({
        query: (request) => ({
          url: `htz/inspection/visual-assessment-catalog/active-catalog`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: ["HtzActiveVisualAssessmentCatalog"],
      }),
      htzVisualAssessmentCatalogShowActiveCatalog: build.query<
        ActiveCatalog,
        ShowActiveCatalogRequest
      >({
        query: (request) => ({
          url: `htz/inspection/visual-assessment-catalog/active-catalog`,
          method: "GET",
          params: request,
        }),
        providesTags: ["HtzActiveVisualAssessmentCatalog"],
      }),
    }),
  });

export const {
  useHtzVisualAssessmentCatalogPutMutation,
  useHtzVisualAssessmentCatalogShowQuery,
  useLazyHtzVisualAssessmentCatalogShowQuery,
  useHtzVisualAssessmentCatalogListQuery,
  useHtzVisualAssessmentCatalogDeleteMutation,
  useHtzVisualAssessmentCatalogPutActiveCatalogMutation,
  useHtzVisualAssessmentCatalogShowActiveCatalogQuery,
  useLazyHtzVisualAssessmentCatalogShowActiveCatalogQuery,
} = visualAssessmentCatalogApi;

export type PutRequest = VisualAssessmentCatalog;

export interface ShowRequest {
  id: string;
}

export interface ListRequest {
  name?: Filter<string>;
  company?: Filter<UUID>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export interface ActiveCatalog {
  companyId: string;
  catalogId: string | null;
}

export interface ShowActiveCatalogRequest {
  companyId: string;
}
